import React from 'react'
import cuid from 'cuid'
import classnames from 'classnames'
import Configurator from '../../../configurator'
import { i18n } from '../../../validator'

/**
 * @description Base input definition
 *      USAGE
 *          <Input
 *              *** PROPS ***
 *          />
 *
 *      PROPS - all props are optional
 *          type                - type of input (email, password, text, time, url,...) (if not defined, default value is text)
 *          id                  - id of input with label (if not defined, id will be generated)
 *          label               - label title (if not defined or null, is not showed)
 *          error               - label with error message (if not defined or null, is not showed)
 *          placeholder         - placeholder of input
 *          disabled            - disable input
 *          value               - default value for input
 *          required            - if input is required
 *          canShowPassword     - if password can be showed in input (only for password type)
 *          inputRef            - ref to input
 *          containerClassName  - additional classes for div container
 *          labelClassName      - additional classes for label
 *          className           - additional classes for input
 *          singleInput         - render only single input element?
 *
 */
@i18n()
export default class Input extends Configurator {
    /**
     * Defined props, what will be removed due ...restProps
     *
     * @type {string[]}
     */
    propsToRemove = [
        'containerClassName',
        'singleInput',
        't',
    ]

    state = {
        id: this.props.id || cuid(),
        isPasswordType: this.props.type === 'password',
        passwordShowed: false,
    }

    togglePasswordVisibility = () => {
        const { passwordShowed } = this.state

        this.setState({
            passwordShowed: !passwordShowed,
        })
    }

    /**
     * Render single input field
     *
     * @param {boolean} withDefaultClassName - render with default className?
     * @return {*}
     */
    renderInput = (withDefaultClassName = true) => {
        const inputProps = Object.assign({}, this.props)

        this.propsToRemove.forEach(propToRemove => {
            if (inputProps?.[propToRemove]) {
                delete inputProps?.[propToRemove]
            }
        })

        const {
            type,
            error,
            placeholder,
            value,
            required,
            inputRef,
            className,
            name,
            autoFocus,
            canShowPassword,
            ...restProps
        } = inputProps

        const {
            id,
            passwordShowed,
        } = this.state

        const inputType = passwordShowed ? 'text' : type

        return (
            <input
                type={inputType || 'text'}
                id={id}
                name={name || ''}
                className={classnames({
                    'form-control': withDefaultClassName,
                    'form-control--error': error,
                    [className]: !!className,
                })}
                placeholder={placeholder || ''}
                value={value || ''}
                required={required}
                ref={inputRef}
                autoFocus={autoFocus}
                {...restProps}
            />
        )
    }

    renderPasswordInput = () => {
        const { t } = this.props

        const { passwordShowed } = this.state

        return (
            <div className="form-type-password">
                {this.renderInput()}
                <span
                    className="form-toggle-password"
                    onClick={this.togglePasswordVisibility}
                >
                    {passwordShowed ? t('hide') : t('show')}
                </span>
            </div>
        )
    }

    /**
     * Render
     *
     * @return {*}
     */
    render = () => {
        const {
            singleInput,
            label,
            error,
            required,
            canShowPassword,
            containerClassName,
            labelClassName,
            t,
        } = this.props

        const {
            id,
            isPasswordType,
        } = this.state

        if (singleInput) {
            return this.renderInput(false)
        }

        return (
            <div className={classnames({
                'form-group': true,
                [containerClassName]: !!containerClassName,
            })}>
                {label &&
                    <label
                        htmlFor={id}
                        className={classnames({
                            [labelClassName]: !!labelClassName,
                        })}
                    >
                        {label}
                        {required &&
                            <small className="text-danger">&nbsp;{t('requiredInBrackets')}</small>
                        }
                    </label>
                }
                {(canShowPassword && isPasswordType) ? this.renderPasswordInput() : this.renderInput()}
                {error &&
                    <div className="form-error-label">{error}</div>
                }
            </div>
        )
    }
}
